import React, { useState } from "react";
import { api } from "../../lib/api";
import { Avatar } from "../common";
import { UserPlus, Trash2, Plus, Shield, Users, Building2, SlidersHorizontal, KeyRound, Link2, Copy, Radio } from "lucide-react";

export default function Settings({ campaignId, campaign, characters, orgs, onReloadCharacters, onReloadOrgs, onReloadCampaign }) {
  const [pf, setPf] = useState({ name: "", player_email: "", player_password: "", fictional_id: "" });
  const [lastCreds, setLastCreds] = useState(null);
  const [newOrg, setNewOrg] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);
  const settings = campaign?.settings || {};
  const [hudForm, setHudForm] = useState(() => {
    const h = campaign?.hud_config || {};
    return {
      enabled: h.enabled !== false,
      fictional_date: h.fictional_date || "",
      time_label: h.time_label || "RT",
      threat_level: h.threat_level || "NOMINAL",
      ticker: Array.isArray(h.ticker) ? h.ticker.join("\n") : "",
    };
  });
  const [hudSaved, setHudSaved] = useState(false);
  async function saveHud() {
    await api.patch(`/campaigns/${campaignId}`, {
      hud_config: {
        enabled: hudForm.enabled,
        fictional_date: hudForm.fictional_date,
        time_label: hudForm.time_label,
        threat_level: hudForm.threat_level,
        ticker: hudForm.ticker.split("\n").map((s) => s.trim()).filter(Boolean),
      },
    });
    setHudSaved(true); setTimeout(() => setHudSaved(false), 2000);
    onReloadCampaign?.();
  }

  async function invite(c) {
    const r = await api.post(`/characters/${c.id}/invite`);
    const link = `${window.location.origin}/invite/${r.data.token}`;
    setInviteLink(link); setCopied(false);
  }
  async function copyLink() {
    try { await navigator.clipboard.writeText(inviteLink); setCopied(true); }
    catch (e) { setCopied(false); }
  }
  async function resetPwd(c) {
    const p = window.prompt(`Nouveau mot de passe pour ${c.name} :`);
    if (!p) return;
    try { await api.patch(`/characters/${c.id}/password`, { password: p }); alert("Mot de passe mis à jour."); }
    catch (e) { alert(e?.response?.data?.detail || "Erreur"); }
  }

  async function createAccess() {
    if (!pf.name.trim()) return;
    await api.post(`/campaigns/${campaignId}/characters`, pf);
    if (pf.player_email) setLastCreds({ email: pf.player_email, password: pf.player_password || "joueur123" });
    setPf({ name: "", player_email: "", player_password: "", fictional_id: "" });
    onReloadCharacters?.();
  }
  async function addOrg() {
    if (!newOrg.trim()) return;
    await api.post(`/campaigns/${campaignId}/organizations`, { name: newOrg.trim() });
    setNewOrg(""); onReloadOrgs?.();
  }
  async function delOrg(id) {
    await api.delete(`/organizations/${id}`); onReloadOrgs?.();
  }
  async function toggle(key) {
    await api.patch(`/campaigns/${campaignId}`, { settings: { ...settings, [key]: !settings[key] } });
    onReloadCampaign?.();
  }

  const set = (k) => (e) => setPf({ ...pf, [k]: e.target.value });

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ background: "var(--r-void)" }}>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <div className="sys-label flex items-center gap-2"><Shield size={12} /> PARAMÈTRES DE LA PARTIE</div>
          <h2 className="font-title font-bold text-xl tracking-tight mt-1">{campaign?.name}</h2>
        </div>

        {/* ACCÈS JOUEURS */}
        <section className="frame p-4">
          <div className="sys-label flex items-center gap-2 mb-3"><Users size={12} /> ACCÈS JOUEURS</div>
          <div className="space-y-2 mb-4">
            {characters.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-2" style={{ background: "var(--r-surface)" }}>
                <Avatar src={c.avatar} name={c.name} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="sys-label truncate" style={{ textTransform: "none", letterSpacing: "normal" }}>
                    {c.player_email ? c.player_email : "— aucun compte —"}{c.fictional_id ? ` · ${c.fictional_id}` : ""}
                  </div>
                </div>
                <button data-testid={`invite-char-${c.id}`} onClick={() => invite(c)} className="btn btn-ghost text-[10px] flex items-center gap-1" title="Générer un lien d'invitation"><Link2 size={11} /> INVITER</button>
                {c.player_email && (
                  <button data-testid={`pwd-char-${c.id}`} onClick={() => resetPwd(c)} className="btn btn-ghost text-[10px] flex items-center gap-1" title="Changer le mot de passe"><KeyRound size={11} /> MDP</button>
                )}
              </div>
            ))}
            {characters.length === 0 && <div className="sys-label">AUCUN JOUEUR</div>}
          </div>

          {inviteLink && (
            <div className="mb-4 p-3" style={{ border: "1px solid var(--r-primary)", background: "color-mix(in srgb, var(--r-primary) 8%, transparent)" }}>
              <div className="sys-label mb-1">LIEN D'INVITATION · À ENVOYER AU JOUEUR</div>
              <div className="flex items-center gap-2">
                <input data-testid="invite-link-field" readOnly className="input font-mono text-xs" value={inviteLink} onFocus={(e) => e.target.select()} />
                <button data-testid="copy-invite-link" onClick={copyLink} className="btn btn-solid px-3 text-[10px] flex items-center gap-1"><Copy size={12} /> {copied ? "COPIÉ" : "COPIER"}</button>
              </div>
            </div>
          )}

          <div className="border-t border-[color:var(--r-primary)]/12 pt-3">
            <div className="sys-label mb-2 flex items-center gap-1"><UserPlus size={12} /> CRÉER UN ACCÈS JOUEUR</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <input data-testid="settings-char-name" className="input" placeholder="Nom du personnage" value={pf.name} onChange={set("name")} />
              <input data-testid="settings-char-fic" className="input font-mono" placeholder="Identifiant fictif" value={pf.fictional_id} onChange={set("fictional_id")} />
              <input data-testid="settings-char-email" className="input font-mono" placeholder="Email de connexion" value={pf.player_email} onChange={set("player_email")} />
              <input data-testid="settings-char-pass" className="input font-mono" placeholder="Mot de passe (défaut joueur123)" value={pf.player_password} onChange={set("player_password")} />
            </div>
            <button data-testid="settings-create-access" className="btn btn-solid w-full mt-3" onClick={createAccess}>CRÉER L'ACCÈS</button>
            {lastCreds && (
              <div className="mt-3 p-3 font-mono text-xs" style={{ border: "1px solid var(--r-primary)", background: "color-mix(in srgb, var(--r-primary) 8%, transparent)" }}>
                ACCÈS CRÉÉ · {lastCreds.email} / {lastCreds.password}
              </div>
            )}
          </div>
        </section>

        {/* ORGANISATIONS */}
        <section className="frame p-4">
          <div className="sys-label flex items-center gap-2 mb-3"><Building2 size={12} /> ORGANISATIONS / FACTIONS</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {orgs.map((o) => (
              <span key={o.id} className="flex items-center gap-2 px-2.5 py-1 text-sm" style={{ border: "1px solid color-mix(in srgb, var(--r-secondary) 40%, transparent)", background: "color-mix(in srgb, var(--r-secondary) 8%, transparent)" }}>
                {o.name}
                <button data-testid={`del-org-${o.id}`} onClick={() => delOrg(o.id)} className="text-[color:var(--r-text-muted)] hover:text-[color:var(--r-alert)]"><Trash2 size={12} /></button>
              </span>
            ))}
            {orgs.length === 0 && <div className="sys-label">AUCUNE ORGANISATION</div>}
          </div>
          <div className="flex gap-2">
            <input data-testid="settings-org-input" className="input" placeholder="Nouvelle organisation" value={newOrg} onChange={(e) => setNewOrg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addOrg()} />
            <button data-testid="settings-org-add" className="btn btn-solid px-4 flex items-center gap-1" onClick={addOrg}><Plus size={14} /> AJOUTER</button>
          </div>
        </section>

        {/* OPTIONS */}
        <section className="frame p-4">
          <div className="sys-label flex items-center gap-2 mb-3"><SlidersHorizontal size={12} /> OPTIONS</div>
          {[
            ["allow_player_edit", "Les joueurs peuvent modifier leurs messages"],
            ["allow_player_delete", "Les joueurs peuvent supprimer leurs messages"],
            ["typing_indicator", "Afficher l'indicateur de frappe"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between py-2 cursor-pointer">
              <span className="text-sm">{label}</span>
              <button
                data-testid={`toggle-${key}`}
                onClick={() => toggle(key)}
                className="w-11 h-6 rounded-full relative transition-colors"
                style={{ background: settings[key] ? "var(--r-primary)" : "var(--r-surface)", border: "1px solid color-mix(in srgb, var(--r-text-muted) 30%, transparent)" }}
              >
                <span className="absolute top-0.5 w-4 h-4 rounded-full transition-all" style={{ left: settings[key] ? 22 : 4, background: settings[key] ? "#04131a" : "var(--r-text-muted)" }} />
              </button>
            </label>
          ))}
        </section>

        {/* HUD JOUEUR */}
        <section className="frame p-4">
          <div className="sys-label flex items-center gap-2 mb-3"><Radio size={12} /> HUD JOUEUR (IMMERSION)</div>
          <label className="flex items-center justify-between py-2 cursor-pointer mb-2">
            <span className="text-sm">Activer le HUD immersif côté joueur</span>
            <button
              data-testid="hud-enabled-toggle"
              onClick={() => setHudForm({ ...hudForm, enabled: !hudForm.enabled })}
              className="w-11 h-6 rounded-full relative transition-colors"
              style={{ background: hudForm.enabled ? "var(--r-primary)" : "var(--r-surface)", border: "1px solid color-mix(in srgb, var(--r-text-muted) 30%, transparent)" }}
            >
              <span className="absolute top-0.5 w-4 h-4 rounded-full transition-all" style={{ left: hudForm.enabled ? 22 : 4, background: hudForm.enabled ? "#04131a" : "var(--r-text-muted)" }} />
            </button>
          </label>
          <div className="grid sm:grid-cols-3 gap-2 mb-2">
            <div>
              <span className="sys-label block mb-1">DATE FICTIVE</span>
              <input data-testid="hud-date-input" className="input font-mono text-xs" placeholder="ex : 2087.142" value={hudForm.fictional_date} onChange={(e) => setHudForm({ ...hudForm, fictional_date: e.target.value })} />
            </div>
            <div>
              <span className="sys-label block mb-1">SUFFIXE HORLOGE</span>
              <input data-testid="hud-timelabel-input" className="input font-mono text-xs" placeholder="RT" value={hudForm.time_label} onChange={(e) => setHudForm({ ...hudForm, time_label: e.target.value })} />
            </div>
            <div>
              <span className="sys-label block mb-1">NIVEAU DE MENACE</span>
              <select data-testid="hud-threat-select" className="input font-mono text-xs" value={hudForm.threat_level} onChange={(e) => setHudForm({ ...hudForm, threat_level: e.target.value })}>
                <option value="NOMINAL">NOMINAL</option>
                <option value="ELEVE">ÉLEVÉ</option>
                <option value="CRITIQUE">CRITIQUE</option>
              </select>
            </div>
          </div>
          <div className="mb-3">
            <span className="sys-label block mb-1">MESSAGES DU BANDEAU · un par ligne (vide = FLUX + générique)</span>
            <textarea data-testid="hud-ticker-input" rows={4} className="input font-mono text-xs resize-none" placeholder={"ALERTE : couvre-feu secteur 7\nMARCHÉ NOIR DE DONNÉES : -12%"} value={hudForm.ticker} onChange={(e) => setHudForm({ ...hudForm, ticker: e.target.value })} />
          </div>
          <button data-testid="hud-save-button" className="btn btn-solid w-full" onClick={saveHud}>{hudSaved ? "✓ ENREGISTRÉ" : "ENREGISTRER LE HUD"}</button>
          <div className="sys-label mt-2" style={{ textTransform: "none", letterSpacing: "normal", color: "var(--r-text-muted)" }}>
            Date vide = date générée automatiquement. Le bandeau affiche tes diffusions FLUX quand il y en a.
          </div>
        </section>
      </div>
    </div>
  );
}
